import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DeskPanel } from "./DeskPanel";
import { Crosshair, Power, PowerOff, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Target, Clock, Activity, X, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem("apex_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const MODE_STYLES = {
  idle:           { bg: "bg-slate-700/40",   text: "text-slate-300", label: "EN VEILLE" },
  scanning:       { bg: "bg-cyan-500/20",    text: "text-cyan-300",  label: "SCAN EN COURS" },
  ordered:        { bg: "bg-amber-500/20",   text: "text-amber-300", label: "ORDRE LIMIT POSÉ" },
  filled:         { bg: "bg-blue-500/20",    text: "text-blue-300",  label: "POSITION OUVERTE" },
  trailing:       { bg: "bg-violet-500/25",  text: "text-violet-200",label: "TRAILING ACTIF" },
  overnight_hold: { bg: "bg-indigo-500/25",  text: "text-indigo-200",label: "HOLD OVERNIGHT" },
  stopped_today:  { bg: "bg-rose-500/20",    text: "text-rose-300",  label: "PAUSE JOURNÉE" },
};

const fmtUsd = (v) => v == null ? "—" : `$${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;

export default function SniperBot() {
  const [status, setStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [liveState, setLiveState] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sniper/status`, { headers: authHeaders(), timeout: 12000 });
      setStatus(r.data);
    } catch (e) { /* silent */ }
  }, []);

  const fetchTrades = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sniper/trades?limit=20`, { headers: authHeaders(), timeout: 10000 });
      setTrades(r.data.trades || []);
    } catch (e) { /* silent */ }
  }, []);

  const fetchLiveState = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/trading212/demo/live-state`, { headers: authHeaders(), timeout: 12000 });
      setLiveState(r.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    let iv;
    const start = async () => {
      await Promise.resolve();
      fetchStatus();
      fetchTrades();
      fetchLiveState();
      iv = setInterval(() => { fetchStatus(); fetchLiveState(); }, 15000);
    };
    start();
    return () => { if (iv) clearInterval(iv); };
  }, [fetchStatus, fetchTrades, fetchLiveState]);

  const handleToggle = async () => {
    setBusy(true);
    try {
      const ep = status?.enabled ? "/api/sniper/disable" : "/api/sniper/enable";
      await axios.post(`${API}${ep}`, {}, { headers: authHeaders() });
      await fetchStatus();
    } catch (e) { alert("Erreur toggle: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const handleScanNow = async () => {
    setBusy(true); setScanResult(null);
    try {
      // 🔧 Async pattern (POST returns task_id, poll status endpoint)
      const r = await axios.post(`${API}/api/sniper/scan-now`, {}, { headers: authHeaders(), timeout: 20000 });
      // Legacy fallback: if backend still returns inline result (wait=true)
      if (r.data && (r.data.best !== undefined || r.data.debug)) {
        setScanResult(r.data.best || { reason: "Aucun ticker ne valide les critères actuellement", debug: r.data.debug });
        setBusy(false);
        return;
      }
      const taskId = r.data?.task_id;
      if (!taskId) {
        setScanResult({ reason: "Aucun task_id retourné" });
        setBusy(false);
        return;
      }
      // Poll every 3s up to 3 min
      let elapsed = 0;
      const poll = setInterval(async () => {
        elapsed += 3;
        try {
          const st = await axios.get(`${API}/api/sniper/scan-now/status/${taskId}`,
            { headers: authHeaders(), timeout: 15000 });
          const s = st.data?.status;
          if (s === "done") {
            clearInterval(poll);
            setScanResult(st.data.best || { reason: "Aucun ticker ne valide les critères actuellement", debug: st.data.debug });
            setBusy(false);
          } else if (s === "error") {
            clearInterval(poll);
            alert("Erreur scan: " + (st.data.error || "unknown"));
            setBusy(false);
          }
        } catch (e) {
          if (e?.response?.status === 404) {
            clearInterval(poll);
            alert("Tâche scan expirée. Relance.");
            setBusy(false);
          }
          // else keep polling on transient errors
        }
        if (elapsed >= 180) {
          clearInterval(poll);
          alert("Scan timeout (3 min). Relance.");
          setBusy(false);
        }
      }, 3000);
    } catch (e) {
      alert("Erreur scan: " + (e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  const handleForceClose = async () => {
    if (!window.confirm("Vendre immédiatement la position en cours au marché ?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/sniper/force-close`, {}, { headers: authHeaders() });
      await fetchStatus(); await fetchTrades();
    } catch (e) { alert("Erreur close: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm("Annuler l'ordre limit en cours ?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/sniper/cancel-order`, {}, { headers: authHeaders() });
      await fetchStatus();
    } catch (e) { alert("Erreur cancel: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const handleReconcile = async () => {
    if (!window.confirm("Réconcilier l'état Sniper avec la vérité T212 ?\n\nNettoie les positions fantômes en DB si T212 n'en a plus. Ne vend RIEN.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/sniper/reconcile`, {}, { headers: authHeaders(), timeout: 20000 });
      const actions = r.data.actions || [];
      if (actions.length === 0) {
        alert("✅ Rien à réconcilier — DB et T212 sont alignés.");
      } else {
        const lines = actions.map(a => {
          if (a.type === "state_cleared") return `• État nettoyé pour ${a.ticker} (${a.reason})`;
          if (a.type === "unknown_t212_positions") return `• T212 a ${a.positions.length} position(s) hors Sniper : ${a.positions.map(p => `${p.ticker} x${p.qty}`).join(", ")}`;
          if (a.type === "ghost_rows_dropped") return `• ${a.count} ligne(s) fantôme supprimée(s)`;
          return `• ${JSON.stringify(a)}`;
        });
        alert("Réconciliation OK :\n\n" + lines.join("\n"));
      }
      await fetchStatus();
    } catch (e) { alert("Erreur réconciliation: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  if (!status) {
    return (
      <div className="bg-slate-900/50 border border-slate-700/40 rounded-xl p-6 text-slate-400" data-testid="sniper-loading">
        <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Chargement Sniper Bot...
      </div>
    );
  }

  const ms = MODE_STYLES[status.mode] || MODE_STYLES.idle;
  const pos = status.current_position;
  const stats = status.today_stats || {};
  const cfg = status.config || {};

  return (
    <div className="space-y-4" data-testid="sniper-root">
      {/* Header card */}
      <div className="bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-slate-800/40 border border-slate-700/50 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500/30 to-violet-500/30 border border-rose-400/40 flex items-center justify-center">
              <Crosshair className="w-6 h-6 text-rose-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Sniper Bot</h2>
              <p className="text-xs text-slate-400">All-In Single Ticker · Trailing Adaptatif · Ride the Winner</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${ms.bg} ${ms.text} border-current/30`} data-testid="sniper-mode">
              {ms.label}
            </span>
            <button
              data-testid="sniper-reconcile"
              disabled={busy}
              onClick={handleReconcile}
              title="Aligne l'état DB avec les positions réelles T212 (nettoie les fantômes, ne vend rien)"
              className="px-3 py-2 rounded-lg font-semibold text-xs transition-all border bg-slate-700/40 border-slate-600/50 text-slate-200 hover:bg-slate-700/60 disabled:opacity-50"
            >
              <RefreshCw className="w-3.5 h-3.5 inline mr-1" />Réconcilier
            </button>
            <button
              data-testid="sniper-toggle"
              disabled={busy}
              onClick={handleToggle}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all border ${
                status.enabled
                  ? "bg-rose-500/20 border-rose-400/40 text-rose-200 hover:bg-rose-500/30"
                  : "bg-emerald-500/20 border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/30"
              } disabled:opacity-50`}
            >
              {status.enabled ? <><PowerOff className="w-4 h-4 inline mr-1.5"/>Désactiver</> : <><Power className="w-4 h-4 inline mr-1.5"/>Activer</>}
            </button>
          </div>
        </div>

        {/* Capital instantané T212 */}
        {status.t212_capital && status.t212_capital.total != null && status.t212_capital.total > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3" data-testid="sniper-capital">
            <StatCard
              label="Capital total T212"
              value={fmtUsd(status.t212_capital.total)}
              sub={`instantané · démo`}
              accent="violet"
            />
            <StatCard
              label="Cash disponible"
              value={fmtUsd(status.t212_capital.free)}
              sub={`prêt à trader`}
              accent={status.t212_capital.free > 500 ? "emerald" : status.t212_capital.free > 0 ? "amber" : "slate"}
            />
            <StatCard
              label="Investi (positions)"
              value={fmtUsd(status.t212_capital.invested)}
              sub={`en marché`}
              accent="cyan"
            />
            <StatCard
              label="PnL latent T212"
              value={fmtUsd(status.t212_capital.ppl)}
              sub={status.t212_capital.invested > 0 ? `${((status.t212_capital.ppl / status.t212_capital.invested) * 100).toFixed(2)}% sur invested` : ""}
              accent={status.t212_capital.ppl > 0 ? "emerald" : status.t212_capital.ppl < 0 ? "rose" : "slate"}
            />
          </div>
        )}

        {/* Stats du jour */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <StatCard label="Trades aujourd'hui" value={`${stats.trades_count || 0}/${cfg.max_trades_per_day || 3}`} />
          <StatCard label="Wins / Losses" value={`${stats.winning_trades || 0} / ${stats.losing_trades || 0}`} />
          <StatCard label="PnL réalisé jour" value={fmtUsd(stats.realized_pnl_usd)} accent={stats.realized_pnl_usd > 0 ? "emerald" : stats.realized_pnl_usd < 0 ? "rose" : "slate"} />
          <StatCard label="Fenêtre d'entrée"  value={status.entry_window_active ? (status.premarket_active ? "🌅 PRE-MARKET" : "✅ SESSION") : "🔒 fermée"} sub={cfg.entry_window_cet + " CET"} />
        </div>

        {stats.circuit_broken && (
          <div className="mt-4 px-3 py-2 bg-rose-500/15 border border-rose-400/30 rounded-lg text-rose-200 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Circuit breaker journée déclenché (-2% capital). Bot en pause jusqu&apos;à demain.
          </div>
        )}
      </div>

      {/* Position en cours */}
      {pos && (pos.ticker) && (
        <div className="bg-slate-900/60 border border-violet-500/30 rounded-2xl p-5" data-testid="sniper-position">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-violet-300 animate-pulse" />
              <h3 className="text-lg font-bold text-white">{pos.ticker}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-200 border border-violet-400/30">
                {pos.trailing_band || "—"}
              </span>
              {pos.order_type && (
                <span className={`text-xs px-2 py-0.5 rounded-full border ${pos.order_type === "market" ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/30" : "bg-amber-500/20 text-amber-200 border-amber-400/30"}`}>
                  {pos.order_type === "market" ? "⚡ MARKET" : "📌 LIMIT"}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {status.mode === "ordered" && (
                <button onClick={handleCancelOrder} disabled={busy} className="px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-400/40 text-amber-200 text-xs font-semibold hover:bg-amber-500/30 disabled:opacity-50" data-testid="sniper-cancel-order">
                  <X className="w-3.5 h-3.5 inline mr-1"/>Annuler limit
                </button>
              )}
              {["filled","trailing","overnight_hold"].includes(status.mode) && (
                <button onClick={handleForceClose} disabled={busy} className="px-3 py-1.5 rounded-lg bg-rose-500/20 border border-rose-400/40 text-rose-200 text-xs font-semibold hover:bg-rose-500/30 disabled:opacity-50" data-testid="sniper-force-close">
                  <X className="w-3.5 h-3.5 inline mr-1"/>Force SELL
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <PosCard label="Quantité" value={pos.qty || "—"} />
            <PosCard label="Fill price" value={fmtUsd(pos.fill_price)} />
            <PosCard label="Prix actuel" value={fmtUsd(pos.live_price || pos.limit_price)} />
            <PosCard label="Plus haut" value={fmtUsd(pos.highest_price)} accent="emerald" />
            <PosCard label="Stop actuel" value={fmtUsd(pos.current_stop)} accent="rose" />
            <PosCard label="PnL %" value={fmtPct(pos.pnl_pct)} accent={pos.pnl_pct >= 0 ? "emerald" : "rose"} highlight />
            <PosCard label="PnL $" value={fmtUsd(pos.pnl_usd)} accent={pos.pnl_usd >= 0 ? "emerald" : "rose"} highlight />
            <PosCard label="Distance au stop" value={pos.live_price && pos.current_stop ? `${((pos.live_price - pos.current_stop)/pos.live_price*100).toFixed(2)}%` : "—"} />
          </div>
        </div>
      )}

      {/* Actions debug */}
      <div className="bg-slate-900/40 border border-slate-700/40 rounded-2xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-400">
            <span className="font-semibold text-slate-300">Dernier scan : </span>
            {status.last_scan_at ? new Date(status.last_scan_at).toLocaleTimeString("fr-FR") : "jamais"}
            {status.last_scan_result && (() => {
              const r = status.last_scan_result;
              if (r.reason === "no_cash") {
                const now = status.t212_capital?.free;
                return (
                  <span className="ml-1">
                    · <span className="text-amber-300">💰 Cash insuffisant</span>
                    <span className="text-slate-500"> (seuil €{Math.round(r.min_required_eur)}, dispo €{Math.round(r.free_cash_eur)})</span>
                    {now != null && now >= r.min_required_eur && (
                      <span className="text-emerald-400 ml-1">· cash OK maintenant €{Math.round(now)}, prochain scan dans &lt;2min</span>
                    )}
                  </span>
                );
              }
              if (r.reason === "pm_watch_only") return <span className="ml-1 text-cyan-300">· ⏳ PM WATCH <b>{r.ticker}</b> {r.pm_day_change > 0 ? '+' : ''}{r.pm_day_change}% · exécution reportée à 09:30 ET</span>;
              if (r.reason === "position_open") return <span className="ml-1">· 🎯 Position déjà ouverte, pas de nouveau scan</span>;
              if (r.reason === "window_closed") return <span className="ml-1">· 🔒 Hors fenêtre d'entrée</span>;
              if (r.reason === "circuit_broken") return <span className="ml-1 text-rose-300">· 🛑 Circuit breaker journée</span>;
              if (r.ticker) return <span className="ml-1 text-emerald-300">· 🎯 Setup trouvé: <b>{r.ticker}</b></span>;
              if (r.reason) return <span className="ml-1 text-slate-500">· {r.reason}</span>;
              return null;
            })()}
          </div>
          <div className="flex gap-2">
            <button onClick={fetchStatus} disabled={busy} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-600/40 text-slate-200 text-xs font-semibold hover:bg-slate-700/60 disabled:opacity-50" data-testid="sniper-refresh">
              <RefreshCw className={`w-3.5 h-3.5 inline mr-1 ${busy ? "animate-spin" : ""}`}/>Refresh
            </button>
            <button onClick={handleScanNow} disabled={busy} className="px-3 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 text-xs font-semibold hover:bg-cyan-500/30 disabled:opacity-50" data-testid="sniper-scan-now">
              <Zap className="w-3.5 h-3.5 inline mr-1"/>Scan now (test)
            </button>
          </div>
        </div>
        {scanResult && (
          <div className="mt-3 px-3 py-2 bg-slate-800/40 border border-slate-600/30 rounded-lg text-xs text-slate-200 font-mono" data-testid="sniper-scan-result">
            {scanResult.ticker ? (
              <>
                🎯 <b>{scanResult.ticker}</b> · prix ${scanResult.price?.toFixed(2)} · jour {fmtPct(scanResult.day_change_pct)} ·
                vol×{scanResult.vol_ratio?.toFixed(2)} · VWAP {scanResult.above_vwap ? "✓" : "✗"} · RSI {scanResult.rsi?.toFixed(0)} ·
                score {scanResult.score?.toFixed(2)}
              </>
            ) : (
              <>Aucun setup trouvé. {scanResult.reason || ""}</>
            )}
          </div>
        )}
      </div>

      <DeskPanel />

      {/* 🎯 T212 LIVE — Positions réelles + ordres pending (toutes sources: Sniper, Briefing Executor, manuel) */}
      <div className="bg-slate-900/50 border border-emerald-700/30 rounded-2xl p-5" data-testid="sniper-t212-live">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-300" />
            T212 Live · Ce qui se passe RÉELLEMENT sur ton compte
          </h3>
          <button onClick={fetchLiveState} className="text-xs px-2 py-1 rounded bg-slate-800/60 hover:bg-slate-700 text-slate-300 flex items-center gap-1" data-testid="sniper-t212-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {/* POSITIONS OUVERTES */}
        <div className="mb-4">
          <div className="text-xs text-slate-400 uppercase mb-2">Positions ouvertes ({liveState?.positions?.length || 0})</div>
          {liveState?.positions?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {liveState.positions.map((p) => (
                <div key={p.ticker} className="bg-slate-950/60 border border-slate-700/40 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold text-white text-sm">{p.ticker_clean}</div>
                    <div className={`text-xs font-semibold ${p.pnl_pct_calc >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtPct(p.pnl_pct_calc)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] text-slate-300">
                    <div className="text-slate-500">Qty</div><div className="text-right">{Number(p.quantity).toFixed(0)}</div>
                    <div className="text-slate-500">PRU</div><div className="text-right">{fmtUsd(p.averagePrice)}</div>
                    <div className="text-slate-500">Cours</div><div className="text-right">{fmtUsd(p.currentPrice)}</div>
                    <div className="text-slate-500">Valeur</div><div className="text-right text-white">{fmtUsd(p.value_usd)}</div>
                    <div className="text-slate-500">PnL</div><div className={`text-right font-semibold ${(p.ppl || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtUsd(p.ppl)}</div>
                  </div>
                  {liveState.orders?.some(o => o.ticker === p.ticker && o.is_stop_for_position) ? (
                    <div className="mt-2 pt-2 border-t border-slate-800 text-[10px] text-emerald-300">
                      🛡️ Stop protégé
                    </div>
                  ) : (
                    <div className="mt-2 pt-2 border-t border-slate-800 text-[10px] text-amber-300">
                      ⚠️ Aucun stop actif
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500">Aucune position ouverte sur T212.</p>
          )}
        </div>

        {/* ORDRES PENDING */}
        <div>
          <div className="text-xs text-slate-400 uppercase mb-2">Ordres pending ({liveState?.orders?.length || 0})</div>
          {liveState?.orders?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-400 border-b border-slate-700/40">
                  <tr>
                    <th className="text-left py-1 px-2">Ticker</th>
                    <th className="text-right py-1 px-2">Qty</th>
                    <th className="text-left py-1 px-2">Type</th>
                    <th className="text-right py-1 px-2">Prix</th>
                    <th className="text-left py-1 px-2">Rôle</th>
                  </tr>
                </thead>
                <tbody>
                  {liveState.orders.map((o) => (
                    <tr key={o.id} className="border-b border-slate-800/40 text-slate-200">
                      <td className="py-1 px-2 font-semibold">{o.ticker_clean || o.ticker?.replace(/_US_EQ$|_EQ$/, "")}</td>
                      <td className="py-1 px-2 text-right">{o.quantity}</td>
                      <td className="py-1 px-2">{o.type}</td>
                      <td className="py-1 px-2 text-right">{fmtUsd(o.limitPrice || o.stopPrice)}</td>
                      <td className="py-1 px-2 text-[10px]">
                        {o.is_stop_for_position ? "🛡️ Stop position" : (o.side === "BUY" ? "🎯 Achat pending" : "💰 Vente pending")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-slate-500">Aucun ordre pending.</p>
          )}
        </div>
      </div>

      {/* Historique des trades */}
      <div className="bg-slate-900/50 border border-slate-700/40 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-violet-300" /> Historique Sniper ({trades.length})
        </h3>
        {trades.length === 0 ? (
          <p className="text-xs text-slate-500">Aucun trade Sniper exécuté pour l&apos;instant.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-400 border-b border-slate-700/40">
                <tr>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Ticker</th>
                  <th className="text-right py-2 px-2">Qty</th>
                  <th className="text-right py-2 px-2">Entry</th>
                  <th className="text-right py-2 px-2">Exit</th>
                  <th className="text-right py-2 px-2">Plus haut</th>
                  <th className="text-right py-2 px-2">PnL %</th>
                  <th className="text-right py-2 px-2">PnL $</th>
                  <th className="text-left py-2 px-2">Raison</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => (
                  <tr key={`${t.ticker}-${t.exited_at || i}`} className="border-b border-slate-800/40 text-slate-200">
                    <td className="py-2 px-2 text-slate-400">{t.exited_at?.slice(5,10)} {t.exited_at?.slice(11,16)}</td>
                    <td className="py-2 px-2 font-bold">{t.ticker}</td>
                    <td className="py-2 px-2 text-right">{t.qty}</td>
                    <td className="py-2 px-2 text-right">{fmtUsd(t.fill_price)}</td>
                    <td className="py-2 px-2 text-right">{fmtUsd(t.exit_price)}</td>
                    <td className="py-2 px-2 text-right text-emerald-300/80">{fmtUsd(t.highest_price)}</td>
                    <td className={`py-2 px-2 text-right font-bold ${t.pnl_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{fmtPct(t.pnl_pct)}</td>
                    <td className={`py-2 px-2 text-right font-bold ${t.pnl_usd >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{fmtUsd(t.pnl_usd)}</td>
                    <td className="py-2 px-2 text-slate-400">{t.exit_reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}

function StatCard({ label, value, sub, accent = "slate" }) {
  const colors = {
    slate: "text-slate-200",
    emerald: "text-emerald-300",
    rose: "text-rose-300",
    violet: "text-violet-200",
    cyan: "text-cyan-300",
    amber: "text-amber-300",
  };
  return (
    <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-base font-bold ${colors[accent] || colors.slate}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function PosCard({ label, value, accent = "slate", highlight = false }) {
  const colors = {
    slate: "text-slate-200",
    emerald: "text-emerald-300",
    rose: "text-rose-300",
  };
  return (
    <div className={`rounded-lg p-3 border ${highlight ? "bg-slate-800/60 border-slate-600/50" : "bg-slate-800/30 border-slate-700/30"}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-base font-bold ${colors[accent]}`}>{value}</p>
    </div>
  );
}
